import React from 'react';
import type { ReviewEntry, ReviewSection } from '../reviewModel';

// The "Final Review" heading and the footer's "Complete & Submit" are frozen
// strings the e2e walk keys on; the answers below are data, hooks are testids.

function EntryValue({ entry }: { entry: ReviewEntry }) {
  if (entry.kind === 'text') return <span className="text-sm text-gray-900 break-words">{entry.value}</span>;
  if (entry.kind === 'image') {
    return (
      <span className="flex items-center gap-3">
        {entry.previewUrl && (
          <img src={entry.previewUrl} alt="" className="h-12 max-w-[160px] rounded-lg border border-gray-200 bg-white object-contain" />
        )}
        <span className="text-sm text-gray-900 break-words">{entry.caption}</span>
      </span>
    );
  }
  return <span className="text-sm text-gray-500">—</span>;
}

export function V2ReviewStep({ sections, onEdit }: {
  sections: ReviewSection[];
  onEdit: (stepIndex: number) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Final Review</h3>
        <p className="text-gray-500 mt-2">Confirm everything looks correct before submitting.</p>
      </div>
      <div className="space-y-3">
        {sections.map((section) => (
          <section key={section.key} data-testid="review-section" className="bg-gray-50 rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {section.subtitle && (
                  <p className="text-xs font-bold text-primary-deep uppercase tracking-widest">{section.subtitle}</p>
                )}
                <p className="text-sm font-bold text-gray-900 break-words">{section.title}</p>
                {section.status && (
                  <p className={`text-xs font-medium ${section.status.ok ? 'text-emerald-600' : 'text-gray-500'}`}>{section.status.text}</p>
                )}
              </div>
              <button
                type="button"
                data-testid="review-edit"
                data-edit-step={section.editStepIndex}
                onClick={() => onEdit(section.editStepIndex)}
                className="shrink-0 min-h-[44px] px-3 -mr-2 rounded-lg text-sm font-semibold text-primary-deep hover:bg-white transition-colors"
              >
                Edit
              </button>
            </div>
            {section.entries.length > 0 && (
              <dl className="mt-3 space-y-2.5">
                {section.entries.map((entry, i) => (
                  <div key={i} data-testid="review-entry" className="min-w-0">
                    <dt className="text-xs text-gray-500 break-words">{entry.label}</dt>
                    <dd className="mt-0.5"><EntryValue entry={entry} /></dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
